import { DecimalPipe } from '@angular/common';
import { Directive, ElementRef, forwardRef, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Utilities } from '../../class/utilities';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';

const CURRENCY_SYMBOL = 'R$';

@Directive({
  standalone: true,
  selector: 'input[money]',
  providers: [
    {
      provide: MAT_INPUT_VALUE_ACCESSOR,
      useExisting: MoneyDirective
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MoneyDirective),
      multi: true,
    }
  ]
})
export class MoneyDirective implements ControlValueAccessor, OnChanges {

  private _value: number | null;
  @Input() decimalPoints: number = 2;
  @Input() noClass: boolean = false;
  @Input() prefix: string = '';
  @Input() suffix: string = '';
  @Input() limit: number;
  @Input() thousandSeparator = '.';
  /** Opt-in: aceita o sinal de menos ("-") digitado, para saldos que podem ser negativos. */
  @Input() allowNegative: boolean = false;
  /** Sinal digitado antes de qualquer dígito (valor ainda 0): mantém o "-" na máscara. */
  private _negativePending = false;


  constructor(
    private elementRef: ElementRef<HTMLInputElement>
  ) { }

  ngOnInit(): void {
    if (!this.noClass) {
      this.elementRef.nativeElement.classList.add('money');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefix'] || changes['suffix']) {
      this.formatValue(this._value);
    }
  }
  
  get value(): number | null {
    return this._value;
  }

  @Input('value')
  set value(value: number | null) {
    this._value = value;
    this.formatValue(value);
  }

  private formatValue(value: number) {
    if (Number.isFinite(value)) {
      const negative = this.allowNegative && (value < 0 || (value === 0 && this._negativePending));
      let maskedValue = new DecimalPipe('pt-BR')
        .transform(Math.abs(value), `1.${this.decimalPoints}-${this.decimalPoints}`)
        .replace(/\./g, this.thousandSeparator);
      let maskedTxt = negative ? "-" : "";
      if (this.prefix) {
        maskedTxt += `${this.prefix}`;
      }
      maskedTxt += maskedValue;
      if (this.suffix) {
        maskedTxt += `${this.suffix}`;
      }
      this.elementRef.nativeElement.value = maskedTxt;
    } else {
      this.elementRef.nativeElement.value = '';
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    const value = (event.target as any).value;
    let unmasked = value.replace(/\D/g, '');

    if (event.inputType === "deleteContentBackward")  {
      if(this.suffix){
        const splited = unmasked.split('');
        splited.pop();
        unmasked = splited.join('');
      }
      else {
        unmasked = unmasked.slice(0);
      }
    }
    const negative = this.allowNegative && /[-\u2212]/.test(value);
    this._negativePending = negative;
    const inputedValue = Number(unmasked) / (Math.pow(10, this.decimalPoints));
    const hasLimit = Number.isFinite(this.limit) && this.limit > 0;
    const limited = hasLimit ? Math.min(inputedValue, this.limit) : inputedValue;
    this.value = negative && limited ? -limited : limited;
    this.formatValue(this.value);
    this._onChange(this.value);
  }

  @HostListener('blur')
  _onBlur() {
    this.formatValue(this.value);
    this._onTouched(); // update form validation
  }

  writeValue(value: any) {
    if (typeof value === 'string' && value !== "") {
      value = Utilities.stringToNumber(value)
    }
    this._value = value;
    this._negativePending = false;
    this.formatValue(this._value);
  }

  _onChange(value: any): void {
    this.value = value;
  }

  _onTouched(): void { }

  registerOnChange(fn: (value: any) => void) {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }
}
